import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ActionSheetController, Platform, ToastController } from '@ionic/angular';
import { filter, ReplaySubject, Subject } from 'rxjs';
import {
  Page,
  TabMeta,
  UserMode,
  TABS_MULTIUSER_META,
  TABS_SINGLEUSER_META,
  TABS_UNKNOWN_META,
} from './tabs-meta';
import { IProperty } from 'src/constants/global-interfaces';
import { AuthenticatedHttpService } from '../authenticatedHttpService.service';
import { App } from '@capacitor/app';
import { HapticsProvider, HapticType } from '../haptics.provider';
import { StorageProvider } from '../storage.provider';
import { ModalProvider } from '../modal.provider';

@Injectable({ providedIn: 'root' })
export class TabsProvider {
  private readonly tabsSingleuserMeta: IProperty<TabMeta> = TABS_SINGLEUSER_META;
  private readonly tabsMultiuserMeta: IProperty<TabMeta> = TABS_MULTIUSER_META;
  private readonly tabsUnknownMeta: IProperty<TabMeta> = TABS_UNKNOWN_META;
  private activeTabsMeta: TabMeta[] = [];

  private readonly _modeChanged = new ReplaySubject<UserMode>();
  public readonly modeChanged$ = this._modeChanged.asObservable();

  private readonly _tabChanged = new Subject<Page>();
  public readonly tabChanged$ = this._tabChanged.asObservable();

  private readonly _activateTab = new Subject<Page>();
  public readonly activateTab$ = this._activateTab.asObservable();

  private readonly _previousTab = new Subject<Page>();
  public readonly previousTab$ = this._previousTab.asObservable();

  private readonly _backButtonEvent = new Subject();
  public readonly backButtonEvent$ = this._backButtonEvent.asObservable();

  private _mode: UserMode = UserMode.Unknown;
  get mode(): UserMode {
    return this._mode;
  }
  get isModeUnknown(): boolean {
    return this._mode === UserMode.Unknown;
  }
  private set mode(value: UserMode) {
    this._mode = value;
  }

  private _activeTab: Page;
  get activeTab(): Page {
    return this._activeTab;
  }
  private set activeTab(value: Page) {
    this._activeTab = value;
  }

  private tabsHistory: Page[] = []; // ?

  get availableTabs(): TabMeta[] {
    return this.activeTabsMeta.filter((t) => t.visible);
  }

  constructor(
    private router: Router,
    private http: AuthenticatedHttpService,
    private modalProdiver: ModalProvider,
    private actionSheetController: ActionSheetController,
    private toastController: ToastController,
    private platform: Platform,
    private storageProvider: StorageProvider,
    private hapticsProvider: HapticsProvider
  ) {
    this.modeChanged$.subscribe((mode) => {
      if (mode === UserMode.SingleUser) {
        this.activeTabsMeta = Object.values(this.tabsSingleuserMeta).sort(
          (a, b) => a.order - b.order
        );
        this.tabsHistory = [];
      } else if (mode === UserMode.MultiUser) {
        this.activeTabsMeta = Object.values(this.tabsMultiuserMeta).sort(
          (a, b) => a.order - b.order
        );
      } else if (mode === UserMode.Unknown) {
        this.activeTabsMeta = Object.values(this.tabsUnknownMeta).sort((a, b) => a.order - b.order);
      } else {
        this.activeTabsMeta = [];
        console.error("can't find user mode");
      }
    });
  }

  subscribeToBackButtonEvent() {
    document.addEventListener(
      'backbutton',
      function (event) {
        event.preventDefault();
        event.stopPropagation();
      },
      false
    );

    if (this.storageProvider.isDesktopPlatform) {
      this.router.events.subscribe(async (event) => {
        if (event instanceof NavigationStart) {
          if (event.navigationTrigger === 'popstate') {
            const hasModal = await this.modalProdiver.getTop();

            if (hasModal) {
              this.router.navigate([this.router.url], { replaceUrl: true });
              await this.forwardBack();
            }
          }
        }
      });
    } else {
      this.platform.backButton.subscribeWithPriority(9999, async () => {
        if (this.isCustomBackButtonEvent) {
          this._backButtonEvent.next(null);
          return;
        }

        const hasModal = await this.modalProdiver.getTop();
        const hasToast = await this.toastController.getTop();

        if (hasToast?.id === 'close-app') {
          await App.exitApp();
        }
        if (this.tabsHistory.length <= 1 && !hasModal) {
          await this.confirmCloseApp();
        } else {
          await this.forwardBack();
        }
      });
    }
  }

  private isCustomBackButtonEvent: boolean;
  enableCustomBackButtonEvent() {
    this.isCustomBackButtonEvent = true;
  }
  disableCustomBackButtonEvent() {
    this.isCustomBackButtonEvent = false;
  }

  subscribeToRouteChange() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const route = this.router.routerState.snapshot.url.split('/');
        route.shift();
        this.autocompleteMode(route[0]);
      });
  }

  setActiveMode(mode: UserMode) {
    this.mode = mode;
    this._modeChanged.next(mode);
  }

  setActiveTab(page: Page) {
    this.activeTab = page;

    if (this.tabsHistory[this.tabsHistory.length - 1] !== this.activeTab) {
      this.tabsHistory.push(this.activeTab);
    }

    this._tabChanged.next(this.activeTab);
  }

  activateTab(page: Page) {
    this._activateTab.next(page);
  }

  setTabVisibility(tab: Page, visibility: boolean) {
    const mode = this.http.getBookingMode();

    if (mode === UserMode.SingleUser) {
      const hasKey = Object.keys(TABS_SINGLEUSER_META).includes(tab);
      if (!hasKey) return;
    } else if (mode === UserMode.MultiUser) {
      const hasKey = Object.keys(TABS_MULTIUSER_META).includes(tab);
      if (!hasKey) return;
    } else {
      const hasKey = Object.keys(TABS_UNKNOWN_META).includes(tab);
      if (!hasKey) return;
    }

    const findTab = this.activeTabsMeta.find((t) => t.tab === tab);
    if (findTab) findTab.visible = visibility;
  }

  isTabVisible(tab: Page): boolean | undefined {
    if ((tab as string) === 'multiduties') {
      return this.isTabVisible(Page.SingleuserDuties);
    }

    const mode = this.http.getBookingMode();

    if (mode === UserMode.SingleUser) {
      const hasKey = Object.keys(TABS_SINGLEUSER_META).includes(tab);
      if (!hasKey) return;
    } else if (mode === UserMode.MultiUser) {
      const hasKey = Object.keys(TABS_MULTIUSER_META).includes(tab);
      if (!hasKey) return;
    } else {
      const hasKey = Object.keys(TABS_UNKNOWN_META).includes(tab);
      if (!hasKey) return;
    }

    const findTab = this.activeTabsMeta.find((t) => t.tab === tab);
    if (findTab) return findTab.visible;

    return false;
  }

  async forwardBack() {
    const actionSheetModal = await this.actionSheetController.getTop();
    if (actionSheetModal) {
      await actionSheetModal.dismiss();
      return;
    }

    const modal = await this.modalProdiver.getTop();
    if (modal) {
      const nav = modal.querySelector('ion-nav') as HTMLIonNavElement | null;
      if (nav && (await nav.canGoBack())) {
        await nav.pop();
        return;
      }

      await this.modalProdiver.dismissTop();
      return;
    }

    const prevPage = this.getPreviousPage();
    if (!prevPage) return;

    this.activeTab = prevPage;
    this._previousTab.next(prevPage);
  }

  private async confirmCloseApp() {
    const activeToast = await this.toastController.getTop();
    await activeToast?.dismiss();

    const toast = await this.toastController.create({
      header: 'Press Back again to close the app',
      buttons: [{ icon: 'log-out-outline', role: 'exit' }],
      id: 'close-app',
      duration: 2000,
      translucent: true,
      icon: 'bulb-outline',
      swipeGesture: 'vertical',
      cssClass: 'custom-bottom-toast',
    });

    toast.onWillDismiss().then(async (data) => {
      if (data.role === 'exit') await App.exitApp();
    });

    await toast.present();
  }

  private getPreviousPage(): Page {
    const mode = this.http.getBookingMode();

    if (
      mode === UserMode.SingleUser &&
      this.tabsHistory[this.tabsHistory.length - 1] === Page.SingleuserBooking
    ) {
      this.tabsHistory = [Page.SingleuserBooking];
    }
    if (
      mode === UserMode.MultiUser &&
      this.tabsHistory[this.tabsHistory.length - 1] === Page.MultiuserBooking
    ) {
      this.tabsHistory = [Page.MultiuserBooking];
    } else if (this.activeTab === this.tabsHistory[this.tabsHistory.length - 1]) {
      this.tabsHistory.pop();
      return this.getPreviousPage();
    }

    return this.tabsHistory[this.tabsHistory.length - 1];
  }

  private autocompleteMode(firstRouteSegment: string) {
    if (firstRouteSegment === 'auth') {
      if (this.mode !== UserMode.Unknown) {
        this.mode = UserMode.Unknown;
        this._modeChanged.next(this.mode);
      }
    }

    const mode = this.http.getBookingMode();

    if (mode === UserMode.SingleUser) {
      if (this.mode !== UserMode.SingleUser) {
        this.mode = UserMode.SingleUser;
        this._modeChanged.next(this.mode);
      }
    } else if (mode === UserMode.MultiUser) {
      if (this.mode !== UserMode.MultiUser) {
        this.mode = UserMode.MultiUser;
        this._modeChanged.next(this.mode);
      }
    }
  }
}
